import { Link } from "react-router-dom";
import { ScaleAndSpeed, DealersFirst, SupportAndTech, MarketingGrowth } from "@/components/shared/DealerAdvantage";
import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { AwardsSection } from "@/components/shared/AwardsSection";
import { BookCallButton } from "@/components/shared/BookCallButton";
import { CheckCircle2, FileText, Store, ArrowRight } from "lucide-react";

const STEPS = [
  ["Talk with our team", "Book a free call. We learn your goals, budget and the market you want, and tell you which ready-to-go stores are available near you."],
  ["Apply to become a Total Wireless dealer", "Fill out the dealer application. We review it and guide you through the Total Wireless and Verizon approval and background check."],
  ["Pick the right location", "We help you choose a spot with strong visibility and foot traffic, or move you into one of our ready-to-go stores."],
  ["Set up your store", "Fixtures, signage, layout, inventory and point-of-sale. We help you build a clean, professional Total Wireless store."],
  ["Get trained", "Hands-on training for you and your staff on plans, activations, devices, sales and daily operations."],
  ["Open your doors", "Grand-opening support and marketing to bring in your first customers, then ongoing field support and live sales tracking in your dealer portal."],
];
const REQUIREMENTS = [
  "Live in or near the market where you want to open",
  "Available investment capital for the store build-out and inventory",
  "A business entity (LLC or corporation) and business bank account",
  "Pass the Total Wireless / Verizon background check",
  "Agree to sell Total Wireless exclusively in the store",
  "Strong customer service skills — no wireless experience required",
];
const DOCUMENTS = ["Business license / entity documents", "Valid driver's license", "Photos of your store or proposed location", "Voided check for ACH payments", "EIN / tax ID"];
export const GUIDE_FAQS = [
  ["How do I open a Total Wireless store?", "Partner with an authorized Total Wireless Master Agent like Prime Media Wireless. You book a call, apply, get approved, choose a location (or a ready-to-go store), set up the store, complete training and open. We guide every step."],
  ["How do I become a Total Wireless dealer?", "Submit the dealer application with Prime Media Wireless. We review it, help you through the Total Wireless approval and background check, and onboard you as an exclusive Total Wireless retailer."],
  ["Do I need wireless experience?", "No. We provide training for you and your team on plans, activations, devices, sales and operations."],
  ["How much does it cost to open a Total Wireless store?", "It depends on the market, the size of the location and whether you take over a ready-to-go store. On a free call we walk you through the real numbers for your area."],
  ["How long does it take to open?", "Timelines depend on approvals and location. Ready-to-go stores can open much faster than a new build-out."],
  ["Do you have stores ready to go?", "Yes. We have stores ready to go in several states. When you contact us we'll tell you what's available in your area."],
  ["What is a Total Wireless Master Agent?", "A Master Agent is authorized by Total Wireless (Verizon Value) to recruit, onboard and support dealers. Prime Media Wireless is recognized as the #1 Growth Master Agent."],
  ["What documents do I need?", "Usually a business license or entity documents, a driver's license, photos of the store, a voided check for ACH and your EIN."],
];

const faqSchema = {
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: GUIDE_FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};

const Hero = () => (
  <section className="bg-navy py-20" data-testid="guide-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
      <p className="label-style text-teal mb-4">Step-by-step guide</p>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">How to Open a Total Wireless Store</h1>
      <p className="text-slate-300 text-base lg:text-lg font-body mb-8 max-w-3xl">Everything you need to become a Total Wireless dealer — the steps, requirements, documents and timeline — from Prime Media Wireless, the #1 Growth Total Wireless Master Agent.</p>
      <div className="flex flex-wrap gap-3">
        <BookCallButton />
        <Link to="/become-dealer#apply" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border-2 border-white/70 text-white hover:bg-white hover:text-navy transition-colors" data-testid="guide-apply-btn">Apply now <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="py-16 bg-white" data-testid="guide-steps">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-8">6 steps to open your Total Wireless store</h2>
      <ol className="space-y-6">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="flex gap-4" data-testid={`guide-step-${i + 1}`}>
            <span className="w-10 h-10 shrink-0 rounded-full bg-teal text-white font-heading font-bold flex items-center justify-center">{i + 1}</span>
            <div><h3 className="font-heading font-semibold text-lg text-navy">{t}</h3><p className="text-slate-600 font-body">{d}</p></div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Checklist = ({ title, items, icon: Icon, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testId}>
    <h2 className="font-heading font-bold text-xl text-navy mb-4">{title}</h2>
    <ul className="space-y-3">{items.map((r) => <li key={r} className="flex gap-3 text-slate-700 font-body"><Icon className="w-5 h-5 text-teal shrink-0 mt-0.5" />{r}</li>)}</ul>
  </div>
);

const ReadyToGo = () => (
  <section className="py-16 bg-teal" data-testid="guide-ready-to-go">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
      <Store className="w-10 h-10 mb-4" />
      <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Skip the build-out: ready-to-go stores</h2>
      <p className="font-body text-white/90 mb-6 max-w-2xl">We have stores ready to go in several states. Contact us and we'll tell you what's available in your area.</p>
      <div className="flex flex-wrap gap-3"><BookCallButton /><Link to="/ready-to-go-stores" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border-2 border-white text-white hover:bg-white hover:text-teal transition-colors">See the program <ArrowRight className="w-4 h-4" /></Link></div>
    </div>
  </section>
);

export const GuideFaq = () => (
  <section className="py-16 bg-slate-50" data-testid="guide-faq">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-8">How to open a Total Wireless store: FAQ</h2>
      <div className="space-y-3">
        {GUIDE_FAQS.map(([q, a], i) => (
          <details key={q} className="group rounded-xl border border-slate-200 bg-white p-5" data-testid={`guide-faq-${i}`}>
            <summary className="cursor-pointer font-heading font-semibold text-navy list-none flex justify-between gap-4">{q}<span className="text-teal group-open:rotate-45 transition-transform">+</span></summary>
            <p className="mt-3 text-slate-600 font-body">{a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export default function HowToOpenStorePage() {
  return (
    <PublicLayout>
      <SEOHead pageSlug="how-to-open-a-total-wireless-store" fallback={{
        meta_title: "How to Open a Total Wireless Store (Step-by-Step Guide) | Prime Media Wireless",
        meta_description: "Learn how to open a Total Wireless store and become a Total Wireless dealer: steps, requirements, documents, ready-to-go stores and FAQs.",
      }} />
      <Helmet><script type="application/ld+json">{JSON.stringify(faqSchema)}</script></Helmet>
      <Hero />
      <Steps />
      <section className="py-16 bg-slate-50"><div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
        <Checklist title="Requirements to become a Total Wireless dealer" items={REQUIREMENTS} icon={CheckCircle2} testId="guide-requirements" />
        <Checklist title="Documents you'll need" items={DOCUMENTS} icon={FileText} testId="guide-documents" />
      </div></section>
      <ReadyToGo />
      <ScaleAndSpeed />
      <DealersFirst />
      <SupportAndTech />
      <MarketingGrowth />
      <AwardsSection />
      <GuideFaq />
    </PublicLayout>
  );
}
